import { useState } from "react";
import { Calendar, Clock, DollarSign, CreditCard, CheckCircle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { format } from "date-fns";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const services = [
  { id: "swedish-60", name: "Swedish Massage", duration: "60 min", price: 80 },
  { id: "swedish-90", name: "Swedish Massage", duration: "90 min", price: 130 },
  { id: "deep-tissue-60", name: "Deep Tissue Massage", duration: "60 min", price: 80 },
  { id: "deep-tissue-90", name: "Deep Tissue Massage", duration: "90 min", price: 130 },
  { id: "hot-stone", name: "Hot Stone Therapy", duration: "75 min", price: 130 },
  { id: "sports-60", name: "Sports Massage", duration: "60 min", price: 80 },
  { id: "sports-90", name: "Sports Massage", duration: "90 min", price: 130 },
  { id: "prenatal", name: "Prenatal Massage", duration: "60 min", price: 80 },
  { id: "aromatherapy-60", name: "Aromatherapy", duration: "60 min", price: 80 },
  { id: "aromatherapy-90", name: "Aromatherapy", duration: "90 min", price: 130 },
  { id: "reflexology", name: "Reflexology", duration: "45 min", price: 65 },
  { id: "relaxation", name: "Relaxation Package", duration: "120 min", price: 195 },
];

const timeSlots = [
  "9:00 AM", "10:00 AM", "11:00 AM", "12:00 PM", 
  "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM", "5:00 PM", "6:00 PM"
];

const DEPOSIT_PERCENTAGE = 0.25; // 25% deposit

export const BookingPage = () => {
  const [step, setStep] = useState(1);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState("");
  const [paymentType, setPaymentType] = useState("deposit"); // deposit or full
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookingComplete, setBookingComplete] = useState(false);

  const selectedServiceData = services.find(s => s.id === selectedService);
  const depositAmount = selectedServiceData ? Math.round(selectedServiceData.price * DEPOSIT_PERCENTAGE) : 0;
  const totalAmount = selectedServiceData ? selectedServiceData.price : 0;
  const amountDue = paymentType === "deposit" ? depositAmount : totalAmount;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const canProceedStep1 = selectedService && selectedDate && selectedTime;
  const canProceedStep2 = formData.firstName && formData.lastName && formData.email && formData.phone;

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    // Simulate booking submission
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    toast.success("Booking confirmed! Check your email for details.");
    setBookingComplete(true);
    setIsSubmitting(false);
  };

  if (bookingComplete) {
    return (
      <div className="min-h-screen bg-white" data-testid="booking-page">
        <Navbar />
        <section className="pt-32 pb-20">
          <div className="max-w-2xl mx-auto px-6 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={40} className="text-green-600" />
            </div>
            <h1 className="section-heading text-4xl font-bold mb-4">Booking Confirmed!</h1>
            <p className="text-slate-600 mb-8">
              Thank you for booking with Millennium Wellness Spa. A confirmation email has been sent to {formData.email}.
            </p>
            <div className="bg-slate-50 p-6 text-left mb-8">
              <h3 className="font-semibold text-[#0F172A] mb-4">Booking Details</h3>
              <div className="space-y-2 text-sm">
                <p><span className="text-slate-500">Service:</span> {selectedServiceData?.name} ({selectedServiceData?.duration})</p>
                <p><span className="text-slate-500">Date:</span> {selectedDate && format(selectedDate, "MMMM d, yyyy")}</p>
                <p><span className="text-slate-500">Time:</span> {selectedTime}</p>
                <p><span className="text-slate-500">Amount Paid:</span> ${amountDue}</p>
                {paymentType === "deposit" && (
                  <p><span className="text-slate-500">Balance Due at Appointment:</span> ${totalAmount - depositAmount}</p>
                )}
              </div>
            </div>
            <p className="text-sm text-slate-500 mb-6">
              Location: 211 E. International Speedway Blvd # 114, Daytona Beach, FL 32118
            </p>
            <Button onClick={() => window.location.href = "/"} className="btn-primary">
              Return Home
            </Button>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="booking-page">
      <Navbar />
      
      {/* Hero */}
      <section className="pt-32 pb-12 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-[#D4AF37] font-medium tracking-widest uppercase mb-4">
            Book Your Session
          </p>
          <h1 className="section-heading text-4xl md:text-5xl font-bold mb-4">
            Schedule Your Massage
          </h1>
          <p className="text-slate-600 max-w-2xl mx-auto">
            Select your service, choose a date and time, and secure your appointment with a deposit or pay in full.
          </p>
        </div>
      </section>

      {/* Progress Steps */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="flex items-center justify-center gap-4 mb-12">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center">
              <div 
                className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-colors ${
                  step >= s ? 'bg-[#D4AF37] text-[#0F172A]' : 'bg-slate-200 text-slate-500'
                }`}
              >
                {s}
              </div>
              {s < 3 && (
                <div className={`w-16 md:w-24 h-1 mx-2 ${step > s ? 'bg-[#D4AF37]' : 'bg-slate-200'}`} />
              )}
            </div>
          ))}
        </div>

        {/* Step 1: Select Service & Date */}
        {step === 1 && (
          <div className="space-y-8 animate-fade-in" data-testid="booking-step-1">
            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-3">
                Select Service
              </label>
              <Select value={selectedService || ""} onValueChange={setSelectedService}>
                <SelectTrigger className="h-12" data-testid="service-select">
                  <SelectValue placeholder="Choose a massage service" />
                </SelectTrigger>
                <SelectContent>
                  {services.map((service) => (
                    <SelectItem key={service.id} value={service.id}>
                      {service.name} ({service.duration}) - ${service.price}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <label className="block text-sm font-medium text-[#0F172A] mb-3">
                  Select Date
                </label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button 
                      variant="outline" 
                      className="w-full h-12 justify-start text-left font-normal"
                      data-testid="date-picker"
                    >
                      <Calendar size={18} className="mr-2 text-slate-500" />
                      {selectedDate ? format(selectedDate, "MMMM d, yyyy") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={selectedDate}
                      onSelect={setSelectedDate}
                      disabled={(date) => date < new Date() || date.getDay() === 0}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#0F172A] mb-3">
                  Select Time
                </label>
                <Select value={selectedTime} onValueChange={setSelectedTime}>
                  <SelectTrigger className="h-12" data-testid="time-select">
                    <SelectValue placeholder="Choose a time slot" />
                  </SelectTrigger>
                  <SelectContent>
                    {timeSlots.map((time) => (
                      <SelectItem key={time} value={time}>{time}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {selectedServiceData && (
              <div className="bg-slate-50 p-6 border border-slate-100">
                <h3 className="font-semibold text-[#0F172A] mb-2">Selected Service</h3>
                <p className="text-slate-600">{selectedServiceData.name} - {selectedServiceData.duration}</p>
                <p className="text-2xl font-bold text-[#D4AF37] mt-2">${selectedServiceData.price}</p>
              </div>
            )}

            <Button 
              onClick={() => setStep(2)} 
              disabled={!canProceedStep1}
              className="btn-secondary w-full md:w-auto"
              data-testid="step1-next"
            >
              Continue
              <ArrowRight size={18} className="ml-2" />
            </Button>
          </div>
        )}

        {/* Step 2: Contact Information */}
        {step === 2 && (
          <div className="space-y-6 animate-fade-in" data-testid="booking-step-2">
            <h2 className="text-2xl font-semibold text-[#0F172A]">Your Information</h2>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#0F172A] mb-2">First Name</label>
                <Input
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder="First name"
                  className="h-12"
                  data-testid="booking-firstname"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0F172A] mb-2">Last Name</label>
                <Input
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  placeholder="Last name"
                  className="h-12"
                  data-testid="booking-lastname"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-2">Email</label>
              <Input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="your@email.com"
                className="h-12"
                data-testid="booking-email"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-2">Phone</label>
              <Input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="(386) 555-0123"
                className="h-12"
                data-testid="booking-phone"
              />
            </div>

            <div className="flex gap-4 pt-4">
              <Button variant="outline" onClick={() => setStep(1)} className="h-12">
                Back
              </Button>
              <Button 
                onClick={() => setStep(3)} 
                disabled={!canProceedStep2}
                className="btn-secondary"
                data-testid="step2-next"
              >
                Continue to Payment
                <ArrowRight size={18} className="ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Payment */}
        {step === 3 && (
          <div className="space-y-6 animate-fade-in" data-testid="booking-step-3">
            <h2 className="text-2xl font-semibold text-[#0F172A]">Payment Options</h2>

            {/* Booking Summary */}
            <div className="bg-slate-50 p-6 border border-slate-100">
              <h3 className="font-semibold text-[#0F172A] mb-4">Booking Summary</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600">Service:</span>
                  <span className="font-medium">{selectedServiceData?.name} ({selectedServiceData?.duration})</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Date:</span>
                  <span className="font-medium">{selectedDate && format(selectedDate, "MMMM d, yyyy")}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Time:</span>
                  <span className="font-medium">{selectedTime}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Client:</span>
                  <span className="font-medium">{formData.firstName} {formData.lastName}</span>
                </div>
                <div className="border-t border-slate-200 my-3" />
                <div className="flex justify-between text-lg">
                  <span className="font-semibold text-[#0F172A]">Total:</span>
                  <span className="font-bold text-[#D4AF37]">${totalAmount}</span>
                </div>
              </div>
            </div>

            {/* Payment Type Selection */}
            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-3">
                Choose Payment Option
              </label>
              <div className="grid md:grid-cols-2 gap-4">
                <button
                  onClick={() => setPaymentType("deposit")}
                  className={`p-6 border text-left transition-all ${
                    paymentType === "deposit" 
                      ? 'border-[#D4AF37] bg-[#D4AF37]/5' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                  data-testid="payment-deposit"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <DollarSign size={20} className={paymentType === "deposit" ? 'text-[#D4AF37]' : 'text-slate-400'} />
                    <span className="font-semibold text-[#0F172A]">Pay Deposit</span>
                  </div>
                  <p className="text-sm text-slate-600 mb-2">
                    Secure your appointment with 25% down
                  </p>
                  <p className="text-2xl font-bold text-[#D4AF37]">${depositAmount}</p>
                  <p className="text-xs text-slate-500 mt-1">
                    Remaining ${totalAmount - depositAmount} due at appointment
                  </p>
                </button>

                <button
                  onClick={() => setPaymentType("full")}
                  className={`p-6 border text-left transition-all ${
                    paymentType === "full" 
                      ? 'border-[#D4AF37] bg-[#D4AF37]/5' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                  data-testid="payment-full"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <CreditCard size={20} className={paymentType === "full" ? 'text-[#D4AF37]' : 'text-slate-400'} />
                    <span className="font-semibold text-[#0F172A]">Pay in Full</span>
                  </div>
                  <p className="text-sm text-slate-600 mb-2">
                    Pay the full amount now
                  </p>
                  <p className="text-2xl font-bold text-[#D4AF37]">${totalAmount}</p>
                  <p className="text-xs text-slate-500 mt-1">
                    Nothing due at appointment
                  </p>
                </button>
              </div>
            </div>

            {/* Amount Due */}
            <div className="bg-[#0F172A] p-6 text-white">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-slate-400 text-sm">Amount Due Now</p>
                  <p className="text-3xl font-bold">${amountDue}</p>
                </div>
                <CreditCard size={32} className="text-[#D4AF37]" />
              </div>
            </div>

            <p className="text-sm text-slate-500">
              By clicking "Confirm & Pay", you agree to our cancellation policy. 
              Cancellations must be made at least 24 hours in advance for a full refund of your deposit.
            </p>

            <div className="flex gap-4 pt-4">
              <Button variant="outline" onClick={() => setStep(2)} className="h-12">
                Back
              </Button>
              <Button 
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="btn-secondary flex-1"
                data-testid="confirm-booking"
              >
                {isSubmitting ? (
                  <>
                    <div className="spinner mr-2" />
                    Processing...
                  </>
                ) : (
                  <>
                    Confirm & Pay ${amountDue}
                    <CreditCard size={18} className="ml-2" />
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default BookingPage;
